import React from 'react';
import { withRouter } from 'react-router-dom';
import CustomCircularProgress from '../common/CustomCircularProgress';

import authService from '../api-authorization/AuthorizeService';
import {
    FormLabel,
    FormBlocker,
    CollapseUnderlineHeader,
    CollapseUnderlineHeaderSmall,
    FlexCenterRow,
    FlexBetweenRow,
    FlexColumnStart,
    FormValidated,
    SmallButton,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    MomentTimeFormat,
} from '../Common';
import { util } from '../Util';
import {
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Container,
    Button,
    Modal,
    ModalHeader,
    ModalFooter,
    ModalBody
} from 'reactstrap';
import './DailyDrivingLogForm.scss';
import TimeEntry from '../common/forms/TimeEntry';
import * as moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faCircleNotch,
    faPlus,
} from '@fortawesome/free-solid-svg-icons';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    SelfDispatchTimesheet,
    SelfDispatchTimesheetDetailViewModel
} from './DailyDrivingLog';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import { ChargeTypeUnits } from '../chargeType/ChargeType';

class DailyDrivingLogForm extends React.Component {

    static contextType = CommonContext;

    constructor(props) {
        super(props);

        const selectedDate = this.props.match.params.id;

        this.state = {
            loading: true,
            saving: false,
            selectedDate: selectedDate,
            jobsOpen: true,
            chargesOpen: true,
            summaryJobsOpen: false,
            summaryChargesOpen: true,
            showAddChargeModal: false,
            showSubmitChargesModal: false,
            showRemoveInternalChargeModal: false,
            addTimeFormValidated: false,
            editable: false,
            removeChargeId: null,
            hourlyJobs: false,
            tonnageJobs: false,
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    async populateState() {
        const { selectedDate } = { ...this.state };
        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) {
            return;
        }

        const tenantUserProfile = await getTenantUserProfile();

        let [dailyEntries, nonFlagContract, contracts, equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.dailyDrivingLog.byDate(selectedDate)),
            util.fetch.js(ApiRoutes.contract.getNonFlagging()),
            util.fetch.js(ApiRoutes.contract.getSelfDispatching()),
            util.fetch.js(ApiRoutes.dailyDrivingLog.getEmployeeEquipment()),
        ]);

        var timesheet = new SelfDispatchTimesheet();
        timesheet.contractId = nonFlagContract.id;
        timesheet.contractDescription = nonFlagContract.description;
        timesheet.nonFlagging = true;
        contracts = [nonFlagContract];

        const userGroups = [
            ...new Set(
                tenantUserProfile.currentUser.appGroups.map(
                    (v) => v.groupName,
                ),
            ),
        ];

        contracts.forEach((contract) => {
            const chargetypes = contract.contractChargeTypes;
            contract.contractChargeTypes = chargetypes.filter((ct) => userGroups.includes(ct.group));
        });

        this.setState((state) => ({
            loading: false,
            saving: false,
            hourlyJobs: false,
            tonnageJobs: false,
            dailyEntries: dailyEntries,
            nonFlagContract,
            contracts,
            equipment,
            timesheet,
            driverName: tenantUserProfile.currentUser.fullName
        }));
    }

    refresh = () => {
        this.populateState();
    }

    onTimeChange = (ev, charge) => {
        const { dailyEntries } = { ...this.state };
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, [MomentTimeFormat.AnteTwelveHour]).format(
                MomentTimeFormat.TwentyFourHourWithLeading,
            )
            : '';

        charge[name] = timeValue;

        this.setState((state) => ({
            dailyEntries: dailyEntries
        }));

        // TODO: Save changes
    };

    onEntryClicked = (event) => {
        if (event.isConfirmed && event.timesheetId > 0)
            this.props.history.push(`${AppNavPaths.Timesheet}/${event.timesheetId}`);
        else
            this.props.history.push(`${AppNavPaths.AssignmentConfirmation}/${event.assignmentId}`);    
    };

    showSubmitChargesModal = () => {
        this.setState((state) => ({
            showSubmitChargesModal: true
        }));
    };

    submitInternalCharges = async () => {
        const { selectedDate } = { ...this.state };

        await util.fetch.post(
            ApiRoutes.dailyDrivingLog.submitInternalCharges(selectedDate),
        );

        this.populateState();

        this.setState((state) => ({
            showSubmitChargesModal: false
        }));
    }

    onAddChargeClicked = () => this.setState({
        editingTimeIndex: null,
        showAddChargeModal: true,
        selectedDetail: new SelfDispatchTimesheetDetailViewModel(),
        addTimeFormValidated: false,
    });

    onEditChargeClicked = (idx, selection) => {

        if (selection.isConfirmed)
            return;

        var selectedDetail = new SelfDispatchTimesheetDetailViewModel();

        selectedDetail.timesheetId = selection.timesheetId;
        selectedDetail.id = selection.id;
        selectedDetail.chargeTypeId = selection.chargeTypeId;
        selectedDetail.chargeTypeName = selection.chargeTypeName;
        selectedDetail.chargeTypeUnitsId = 1;
        selectedDetail.chargeTypeUnitsName = 'Hours';
        selectedDetail.start = selection.startTime;
        selectedDetail.end = selection.endTime;
        selectedDetail.equipmentId = selection.equipmentId;
        selectedDetail.equipmentName = selection.equipmentName;

        this.setState({
            editingTimeIndex: idx,
            showAddChargeModal: true,
            selectedDetail,
            addTimeFormValidated: false,
        });
    };

    onChargeTypeChanged = async (selection) => {
        const { selectedDetail } = { ...this.state };

        if (!selection) {
            selectedDetail.start = null;
            selectedDetail.end = null;
            selectedDetail.chargeTypeId = null;
            selectedDetail.chargeTypeUnitsId = null;
            selectedDetail.chargeTypeUnitsName = null;
        } else {
            selectedDetail.chargeTypeId = selection.chargeTypeId;
            selectedDetail.chargeTypeName = selection.description;
            const chargeTypeUnitsId = selection.chargeUnits === 'Hours' ? ChargeTypeUnits.Hours
                : selection.chargeUnits === 'Flat' ? ChargeTypeUnits.Flat
                    : ChargeTypeUnits.Tonnage;
            selectedDetail.chargeTypeUnitsId = chargeTypeUnitsId;
            selectedDetail.chargeTypeUnitsName = selection.chargeUnits;
        }

        if (selection.equipment) {
            await this.setState({ equipmentLoading: true });
            const equipmentOptions = await util.fetch.js(
                ApiRoutes.selfDispatchTimesheet.equipmentByChargeType(
                    selection.chargeTypeId,
                ),
            );
            this.setState({ equipmentOptions, equipmentLoading: false });
        }
        this.setState({ selectedDetail });
    };

    onEquipmentChanged = async (selection) => {
        const { selectedDetail } = { ...this.state };

        selectedDetail.equipmentId = selection.id;
        selectedDetail.equipmentName = selection.description;

        this.setState({ selectedDetail, selection });
    };

    getDuration = (date, start, end, breakStart, breakEnd, bPrint) => {
        // Print duration between start and end time
        const totalDuration = '0h 00m';
        const dateFormat = 'YYYY-MM-DD HH:mm';

        if (start && end) {
            const mDate = moment(date).format('YYYY-MM-DD');
            const strStart = `${mDate} ${start}`;
            const strEnd = `${mDate} ${end}`;

            const mStartTime = moment(strStart, dateFormat);
            const mEndTime = moment(strEnd, dateFormat);

            // Account for times stretching into the next morning/day.
            if (mEndTime.isBefore(mStartTime)) {
                mEndTime.add(1, 'day');
            }

            const duration = moment.duration(mEndTime.diff(mStartTime));

            let breakDurationString = '';

            if (breakStart && breakEnd) {
                const breakStartString = `${mDate} ${breakStart}`;
                const breakEndString = `${mDate} ${breakEnd}`;

                const breakStartTime = moment(breakStartString, dateFormat);
                const breakEndTime = moment(breakEndString, dateFormat);

                if (breakEndTime.isBefore(breakStartTime)) {
                    breakEndTime.add(1, 'day');
                }

                const breakDuration = moment.duration(
                    breakEndTime.diff(breakStartTime),
                );

                duration.subtract(breakDuration);

                const breakTotalMinutes = parseInt(breakDuration.asMinutes());

                const breakHours = Math.floor(breakTotalMinutes / 60);
                const breakMinutes = (breakTotalMinutes % 60)
                    .toString()
                    .padStart(2, '0');

                breakDurationString = breakHours > 0
                    ? ` (${breakHours}h ${breakMinutes}m break)`
                    : ` (${breakMinutes}m break)`;
            }

            const totalMinutes = parseInt(duration.asMinutes());

            if (totalMinutes >= 0) {
                const hours = Math.floor(totalMinutes / 60);
                const mins = (totalMinutes % 60).toString().padStart(2, '0');

                if (bPrint) return `${hours}h ${mins}m${breakDurationString}`;
                return totalMinutes;
            }
        }
        return bPrint ? totalDuration : 0.0;
    };

    onChargeChange = (ev) => {
        const { selectedDetail } = { ...this.state };
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, [MomentTimeFormat.AnteTwelveHour]).format(
                MomentTimeFormat.TwentyFourHourWithLeading,
            )
            : '';

        selectedDetail[name] = name === 'value' ? val : timeValue;

        if (['breakStart', 'breakEnd'].some((v) => v == name) && !timeValue) {
            selectedDetail.breakStart = '';
            selectedDetail.breakEnd = '';
        }

        this.setState({
            selectedDetail,
            addTimeFormValidated: false /* reset validation to avoid highlights when changing info */,
        });
    };

    timeEntryOverlaps = (entry, timeEntries) => {
        let overlaps = false;

        if (!timeEntries.length) return overlaps;

        timeEntries.sort(this.compareTimeDetailVm);

        const entryStart = this.formatTime24Hr(entry.start);
        const entryEnd = this.formatTime24Hr(entry.end);
        for (const e of timeEntries) {
            const start = this.formatTime24Hr(e.start);
            const end = this.formatTime24Hr(e.end);
            if (
                // Test the new entry against the iterated item
                (entryStart.isAfter(start) && entryStart.isBefore(end))
                || (entryEnd.isAfter(start) && entryEnd.isBefore(end))
                // Test the iterated item against the new entry.
                // Must be done in case the new entry is outside the bounds of the iterated item
                // e.g. 12AM - 6PM against 6:30AM - 12PM
                || (start.isAfter(entryStart) && start.isBefore(entryEnd))
                || (end.isAfter(entryStart) && end.isBefore(entryEnd))
            ) {
                overlaps = true;
                break;
            }
        }
        return overlaps;
    };

    formatTime24Hr = (stringNumber) => moment(stringNumber, MomentTimeFormat.TwentyFourHourWithLeading);

    onUpdateCharge = async (e) => {

        const {
            timesheet, contracts, selectedDetail, editingTimeIndex, selectedDate
        } = {
            ...this.state,
        };

        const selectedContract = (contracts ?? []).find(
            (x) => x.id === timesheet?.contractId,

        );
        const selectedContractChargeType = (selectedContract?.contractChargeTypes ?? []).find(
            (x) => x.chargeTypeId === selectedDetail?.chargeTypeId,

        ) ?? '';

        const creatingNew = Boolean(Number.isNaN(parseInt(editingTimeIndex)));
        timesheet.date = moment(selectedDate, 'YYYYMMDD')

        if (selectedDetail.chargeTypeId == null) {
            toasty.error('Please selected a charge type');
            return false;
        }

        if (selectedDetail.equipmentId == null &&
            (selectedContractChargeType.accountCodingTypeId == 2 || selectedContractChargeType.accountCodingTypeId == 5 || selectedContractChargeType.accountCodingTypeId == 4)) {
            toasty.error('Please select equipment');
            return false;
        }

        if (selectedDetail.chargeTypeUnitsName === 'Hours') {
            // Block end time being earlier than start time (same-day comparison).
            // getDuration treats end-before-start as a midnight-crossing shift and
            // adds a day, so we have to guard separately here.
            //if (selectedDetail.start && selectedDetail.end) {
            //    const startMoment = moment(selectedDetail.start, ['H:mm', 'HH:mm']);
            //    const endMoment = moment(selectedDetail.end, ['H:mm', 'HH:mm']);

            //    if (startMoment.isValid() && endMoment.isValid() && endMoment.isBefore(startMoment)) {
            //        toasty.error('End time cannot be before the start time.');
            //        return false;
            //    }
            //}

            // Validate the entry itself.  A valid range will have a duration > 0.
            const rangeDuration = this.getDuration(
                timesheet.date,
                selectedDetail.start,
                selectedDetail.end,
                selectedDetail.breakStart,
                selectedDetail.breakEnd,
                false,
            );
            if (rangeDuration <= 0) {
                toasty.error('Please enter a valid time range.');
                return false;
            }

            // If we are not editing, attempt to push the entry.
            if (creatingNew) {
                // RLC: Check if new entry will overlap.
                if (
                    !this.timeEntryOverlaps({ ...selectedDetail }, [
                        ...timesheet.timeEntries,
                    ])
                ) {
                    selectedDetail.value = rangeDuration;
                    timesheet.timeEntries.push({ ...selectedDetail });
                } else {
                    toasty.error(
                        'This time entry overlaps entries you\'ve already added for this date.',
                    );
                    return false;
                }

                // Otherwise, check to see if the edit will cause a time overlap.
            } else {
                // RLC: Spread and splice, to avoid mutating state while we check the newly modified entry.
                //     Remove the edited entry from the array first, only compare against the other entries.
                const toCheck = [...timesheet.timeEntries];
                toCheck.splice(editingTimeIndex, 1);

                if (
                    !this.timeEntryOverlaps({ ...selectedDetail }, [
                        ...toCheck,
                    ])
                ) {
                    // We are all good, overwrite the new state.
                    timesheet.id = selectedDetail.timesheetId;
                    timesheet.equipmentId = selectedDetail.equipmentId;
                    timesheet.equipmentName = selectedDetail.equipmentName;
                    selectedDetail.value = rangeDuration;

                    timesheet.timeEntries.push({
                        ...selectedDetail,
                    });
                } else {
                    toasty.error(
                        'This time entry overlaps entries you\'ve already added for this date.',
                    );
                    return false;
                }
            }
        } else if (creatingNew) timesheet.timeEntries.push({ ...selectedDetail });
        else timesheet.timeEntries[editingTimeIndex] = { ...selectedDetail };

        if (creatingNew) {
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.selfDispatchTimesheet.create(),
                { ...timesheet },
                'Error Creating Timesheet',
            );
        }

        else {
            await util.fetch.post(
                ApiRoutes.dailyDrivingLog.updateInternalCharge(),
                { ...timesheet },
                'Error Saving Timesheet',
            );
        }

        this.setState({
            timesheet,
            selectedDetail: null,
            showAddChargeModal: false,
        });

        this.populateState();
    };

    setSaving = (b) => this.setState({ saving: b });

    onRemoveInternalChargeClicked = (ev, id) => {

        ev.stopPropagation(); 

        this.setState({
            removeChargeId: id,
            showRemoveInternalChargeModal: true
        });
    }

    toggleRemoveChargeModal = () => {
        const { showRemoveInternalChargeModal } = { ...this.state };

        this.setState({
            showRemoveInternalChargeModal: !showRemoveInternalChargeModal
        });
    }

    onRemoveInternalCharge = async () => {
        const { removeChargeId } = { ...this.state };

        await util.fetch.post(ApiRoutes.dailyDrivingLog.removeCharge(removeChargeId));

        this.setState({
            removeChargeId: null,
            showRemoveInternalChargeModal: false
        });

        this.refresh();
    }

    openTimesheet = (e) => {

    };

    calcTime = (startDate, endDate) => {
        const { selectedDate } = { ...this.state };

        if (startDate && endDate) {

            var start = moment(startDate).format('h:mm a');
            var end = moment(endDate).format('h:mm a');

            return this.getDuration(selectedDate, start, end, null, null, true);
        }

        return '0h 00m';
    }

    getTotalChargeHours = () => {
        const { dailyEntries, selectedDate } = { ...this.state };

        var totalMinutes = 0;

        const charges = dailyEntries.filter((x) => x.logEntryType == 2);
        for (var ic = 0; ic < charges.length; ic++) {
            var internalCharge = charges[ic];
            if (internalCharge.removed) {
                continue;
            }

            totalMinutes += this.getDuration(selectedDate, internalCharge.startTime, internalCharge.endTime);
        }

        const hours = Math.floor(totalMinutes / 60);
        const mins = (totalMinutes % 60).toString().padStart(2, '0');
        return `${hours}h ${mins}m`;
    }

    getTotalHours = () => {
        const { dailyEntries } = { ...this.state };
        var totalHours = 0;

        // Internal
        const charges = (dailyEntries ?? []).filter((x) => x.logEntryType == 2);
        for (var ch = 0; ch < charges.length; ch++) {

            if (charges[ch].removed == true)
                continue;

            totalHours += charges[ch].value;
        }

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        // Hourly
        for (var hj = 0; hj < jobs.length; hj++) {
            var hourlyJob = jobs[hj];
            const hourlyTickets = (hourlyJob.scaleTickets ?? []).filter((x) => x.isTonnage == false);

            for (var t = 0; t < hourlyTickets.length; t++) {
                var hourlyCharges = hourlyTickets[t].charges;

                for (var hc = 0; hc < hourlyCharges.length; hc++) {
                    if (hourlyCharges[hc].resourceTypeId == 1 && hourlyCharges[hc].removed != true)
                        totalHours += hourlyCharges[hc].value;
                }
            }
        }

        // Tonnage
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            const scaleTickets = (job.scaleTickets ?? []).filter((x) => x.isTonnage == true);

            var start = null;
            for (var s = 0; s < scaleTickets.length; s++) {
                var scaleTicket = scaleTickets[s];

                for (var c = 0; c < scaleTicket.charges.length; c++) {
                    var charge = scaleTicket.charges[c];

                    if (charge.chargeTypeUnitsId == 3 || charge.linkedChargeTypeId > 0 || charge.removed == true)
                        continue;

                    if (start == null && charge.start != null) {
                        start = charge.start;
                    }

                    if (charge.end != null) {

                        var startDt = moment(start);
                        var startTimeStr = startDt.format('h:mm a');
                        const startTime = moment.utc(startTimeStr, 'h:mm a');

                        var endDt = moment(charge.end);
                        var endTimeStr = endDt.format('h:mm a');
                        const endTime = moment.utc(endTimeStr, 'h:mm a');

                        var duration = moment.duration(endTime.diff(startTime));
                        if (duration.asHours() > 0 && duration.asHours() < 24)
                            totalHours += duration.asHours();

                        start = null;
                    }
                }
            }
        }

        return totalHours.toFixed(2);
    }

    getTotalTonnage = () => {
        const { dailyEntries } = { ...this.state };

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        var totalTonnage = 0;
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            for (var s = 0; s < job.scaleTickets.length; s++) {
                var scaleTicket = job.scaleTickets[s];
                for (var c = 0; c < scaleTicket.charges.length; c++) {
                    var charge = scaleTicket.charges[c];
                    if (charge.chargeTypeUnitsId == 3 && charge.removed != true) {
                        totalTonnage += charge.tonnage;
                    }
                }
            }
        }

        return totalTonnage;
    }

    getTotalLoads = () => {
        const { dailyEntries } = { ...this.state };

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        var totalLoads = 0;
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            for (var s = 0; s < job.scaleTickets.length; s++) {
                var scaleTicket = job.scaleTickets[s];
                if (scaleTicket.isDeleted)
                    continue;

                if (scaleTicket.isTonnage) {
                    for (var c = 0; c < scaleTicket.charges.length; c++) {
                        var charge = scaleTicket.charges[c];

                        if (charge.chargeTypeUnitsId == 1 && charge.removed != true) {
                            totalLoads++;
                        }
                    }
                }

                else {
                    for (var c2 = 0; c2 < scaleTicket.charges.length; c2++) {
                        var charge2 = scaleTicket.charges[c2];

                        if (charge2.chargeTypeUnitsId == 1 && charge2.removed != true) {
                            totalLoads += charge2.loads;
                        }
                    }
                }
            }
        }

        return totalLoads;
    }

    render() {
        if (this.state.loading) {
            return (
                <div className="confirmationContainer h-100 pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting log...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }
        return this.renderForm();
    }

    renderForm() {

        const {
            dailyEntries,
            saving,
            jobsOpen,
            chargesOpen,
            selectedDate,
            showAddChargeModal,
            addTimeFormValidated,
            selectedDetail,
            contracts,
            timesheet,
            editingTimeIndex,
            showSubmitChargesModal,
            summaryJobsOpen,
            summaryChargesOpen,
            showRemoveInternalChargeModal,
            equipment,
            driverName
        } = { ...this.state };

        var formattedDate = moment(selectedDate, 'YYYYMMDD').format('dddd, MMMM Do YYYY');

        const jobs = dailyEntries.filter((x) => x.logEntryType == 1);
        const charges = dailyEntries.filter((x) => x.logEntryType == 2 && x.removed !== true);
        const unconfirmedCharges = charges.filter((x) => !x.isConfirmed);

        const selectedContract = (contracts ?? []).find(
            (x) => x.id === timesheet?.contractId,
        );

        const selectedEquipment = (equipment ?? []).find(
            (x) => x.id === selectedDetail?.equipmentId,
        );

        const selectedContractChargeType = (selectedContract?.contractChargeTypes ?? []).find(
            (x) => x.chargeTypeId === selectedDetail?.chargeTypeId,
        ) ?? '';

        const selectedIncrement = selectedContractChargeType?.configurations
            ? selectedContractChargeType.configurations[0].increment
            : '';

        const totalTonnage = this.getTotalTonnage();

        return (
            <>
                <div className='dailyDrivingLog confirmationContainer h-100 pt-2'>
                    <Container size="sm">

                        <FormBlocker showProgress show={!!saving} />

                        <div className="title"><span className="fa fa-calendar"></span>&nbsp;&nbsp;{formattedDate}</div>

                        <table style={{ fontSize: '13px', marginBottom: '15px' }}>
                            <tr>
                                <td width='65'><b>Driver:</b></td><td>{driverName}</td>
                            </tr>
                            <tr>
                                <td><b>Hours:</b></td><td>{this.getTotalHours()}</td>
                            </tr>
                            <tr>
                                <td><b>Tonnage:</b></td><td>{totalTonnage.toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td><b>Loads:</b></td><td>{this.getTotalLoads()}</td>
                            </tr>
                        </table>

                        <CollapseUnderlineHeader
                            headerText='Jobs'
                            isOpen={jobsOpen}
                            toggleCollapse={() => this.setState({
                                jobsOpen: !jobsOpen,
                            })}                >
                            {jobs.map((d, idx) => (
                                <>

                                    {d.logEntryType == 1 && (
                                        <Card color={!d.isConfirmed || !d.isComplete ? 'danger' : 'primary'}>
                                            <CardHeader onClick={() => this.onEntryClicked(d)}>
                                                <table width='100%'>
                                                    <tr>
                                                        <td>
                                                            <span className='entryLogHeader'>{d.customer} - {d.contractNumber}</span>
                                                            
                                                            {d.workOrderNumber.length > 0 &&
                                                                <span className='entryLogHeader'>&nbsp;&nbsp;#{d.workOrderNumber}</span>
                                                            }
                                                        </td>
                                                        <td align='right'>
                                                            <span className='entryLogStatus'>
                                                                {!d.isConfirmed ? 'Unconfirmed' : !d.isComplete ? 'Incomplete' : 'Completed'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </table>
                                            </CardHeader>
                                            <CardBody style={{ paddingTop: '5px' }}>

                                                <div onClick={() => this.onEntryClicked(d)}>
                                                    <table width='100%'>
                                                        <tr>
                                                            <td width='80' className="label" align='right'>Start:</td>
                                                            <td>{moment(d.startDate).format('h:mm a')}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="label" align='right'>Haul From:</td>
                                                            <td>{d.haulFrom}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="label" align='right'>Haul To:</td>
                                                            <td>{d.haulTo}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="label" align='right'>Materials:</td>
                                                            <td>{d.materials}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="label" align='right'>Notes:</td>
                                                            <td>{d.notes}</td>
                                                        </tr>
                                                    </table>
                                                </div>

                                                {d?.scaleTickets?.length > 0 &&
                                                    <CollapseUnderlineHeaderSmall
                                                        headerText='Hours'
                                                        isOpen={d.showHourlyJobs}
                                                        toggleCollapse={() => {
                                                            d.showHourlyJobs = !d.showHourlyJobs;

                                                            this.setState({
                                                                dailyEntries: dailyEntries,
                                                            })
                                                        }}
                                                    >
                                                        <table width='100%'>
                                                            <tr>
                                                                <td width='100%'>
                                                                    <table width='100%'>
                                                                        {d.scaleTickets.map((st, idx) => {

                                                                            var lastStart = '';

                                                                            return (
                                                                                <tr>
                                                                                    <td>
                                                                                        <table width='100%'>
                                                                                            {st.charges.map((c, idx) => {

                                                                                                if ((c.resourceTypeId == 2) && c.removed != true) {

                                                                                                    var start = (c.start != null ? moment(c.start).format('h:mm a') : '---') + ' - ' + (c.end != null ? moment(c.end).format('h:mm a') : '');

                                                                                                    if (lastStart == start) {
                                                                                                        start = '';
                                                                                                    }

                                                                                                    if (c.materialName == null)
                                                                                                        c.materialName = '';

                                                                                                    lastStart = start;
                                                                                                }

                                                                                                return (
                                                                                                    <>
                                                                                                        {(c.resourceTypeId == 2) && c.removed != true && start.length > 0 && 
                                                                                                            <tr> 
                                                                                                                <td width='100%' style={{ paddingTop: '5px', borderTop: '1px solid #C0C0C0' }}>
                                                                                                                    <div>
                                                                                                                        {st.workOrderNumber.length > 0 &&
                                                                                                                            <span>Ticket: #{st.workOrderNumber}: &nbsp;</span>
                                                                                                                        }

                                                                                                                        {start}

                                                                                                                        <br></br>
                                                                                                                        {c.chargeTypeUnitsId == 1 &&
                                                                                                                            <span>&nbsp;&nbsp;{c.materialName + ' (' + c.loads + ' loads)'}</span>
                                                                                                                        }
                                                                                                                        {c.chargeTypeUnitsId == 3 && c.materialName.length > 0 &&
                                                                                                                            <span>&nbsp;&nbsp;{c.materialName + ' (' + c.value + ' tons)'}</span>
                                                                                                                        }
                                                                                                                    </div>
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        }

                                                                                                        {(c.resourceTypeId == 2) && c.removed != true && start.length == 0 &&
                                                                                                            <tr>
                                                                                                                <td width='100%'>
                                                                                                                    <div>
                                                                                                                        {st.workOrderNumber.length > 0 &&
                                                                                                                            <span>Ticket: #{st.workOrderNumber}: &nbsp;</span>
                                                                                                                        }
                                                                                                                        {c.chargeTypeUnitsId == 1 &&
                                                                                                                            <span>&nbsp;&nbsp;{c.materialName + ' (' + c.loads + ' loads)'}</span>
                                                                                                                        }
                                                                                                                        {c.chargeTypeUnitsId == 3 && c.materialName.length > 0 &&
                                                                                                                            <span>&nbsp;&nbsp;{c.materialName + ' (' + c.value + ' tons)'}</span>
                                                                                                                        }
                                                                                                                    </div>
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        }
                                                                                                    </>
                                                                                                )
                                                                                            })}
                                                                                        </table>
                                                                                    </td>
                                                                                </tr>

                                                                            )
                                                                        })}
                                                                    </table>
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </CollapseUnderlineHeaderSmall>
                                                }

                                                <table width='100%'>
                                                    <tr>
                                                        <td width='100%' align='right'>
                                                            <SmallButton
                                                                type="button"
                                                                className="ml-1"
                                                                title=""
                                                                onClick={() => this.props.history.push(
                                                                    `${AppNavPaths.Timesheet}/${d.timesheetId}`,
                                                                )}
                                                            >
                                                                <i className="fa fa-plus-circle fa-lg mr-1" />
                                                                Add Charge
                                                            </SmallButton>
                                                        </td>
                                                    </tr>
                                                </table>
                                                

                                            </CardBody>
                                        </Card>
                                    )}
                                </>
                            ))}

                            {jobs.length == 0 &&
                                <div>No Jobs</div>
                            }
                        </CollapseUnderlineHeader>

                        <div>&nbsp;</div>

                        <CollapseUnderlineHeader
                            headerText='Internal Charges'
                            isOpen={chargesOpen}
                            toggleCollapse={() => this.setState({
                                chargesOpen: !chargesOpen,
                            })}>

                            {charges.map((d, idx) => (
                                <>
                                    {d.logEntryType == 2 && (
                                        <Card color={d.isConfirmed ? 'primary' : 'danger'}>
                                            <CardHeader>
                                                <table width='100%'>
                                                    <tr>
                                                        <td><span className='entryLogHeader'>{d.chargeTypeName}</span></td>
                                                        <td align='right'>
                                                            <span className='entryLogStatus'>
                                                                {d.isConfirmed ? 'Submitted' : 'Not Submitted'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </table>
                                            </CardHeader>
                                            <CardBody style={{ paddingLeft: '10px', cursor: 'pointer' }} onClick={() => this.onEditChargeClicked(idx, d)}>

                                                <div className='d-flex'>

                                                    <div className="pt-2 flex-fill" style={{ pointerEvents: 'none' }}>
                                                        <table>
                                                            <tr>
                                                                <td className="label">
                                                                    <div style={{ width: '120px' }}>
                                                                        <FormGroup>
                                                                            <FormLabel
                                                                                htmlFor="startTime"
                                                                                text="Start Time"
                                                                            />
                                                                            <TimeEntry
                                                                                disabled={true}
                                                                                name="startTime"
                                                                                value={d.startTime}
                                                                                increment={0.083}
                                                                                onChange={(e) => {
                                                                                    this.onTimeChange(e, d)
                                                                                }}
                                                                            />
                                                                        </FormGroup>
                                                                    </div>
                                                                </td>

                                                                <td className="label">
                                                                    <div style={{ width: '120px' }}>
                                                                        <FormGroup>
                                                                            <FormLabel
                                                                                htmlFor="endTime"
                                                                                text="End Time"
                                                                            />
                                                                            <TimeEntry
                                                                                disabled={true}
                                                                                name="endTime"
                                                                                value={d.endTime}
                                                                                increment={0.083}
                                                                                onChange={(e) => {
                                                                                    this.onTimeChange(e, d)
                                                                                }}
                                                                            />
                                                                        </FormGroup>
                                                                    </div>
                                                                </td>

                                                                {!d.isConfirmed &&
                                                                    <td width="100%" align="right" style={{ pointerEvents: 'auto' }}>
                                                                        <div
                                                                            onClick={(ev) => this.onRemoveInternalChargeClicked(ev, d.id)}
                                                                            style={{ paddingRight: '20px', fontSize: '16px', display: 'inline-block', cursor: 'pointer' }}
                                                                            title="Remove charge"
                                                                        >
                                                                            <i class='fa fa-trash'></i>
                                                                        </div>
                                                                    </td>
                                                                }
                                                            </tr>
                                                        </table>
                                                    </div>
                                                </div>

                                            </CardBody>
                                        </Card>
                                    )}
                                </>
                            ))}

                            {charges.length == 0 &&
                                <div>No Internal Charges</div>
                            }

                        </CollapseUnderlineHeader>
                    </Container>
                </div>

                <FlexCenterRow className="userActions p-1">

                    {(charges.length == 0 || (charges.length > 0 && unconfirmedCharges.length > 0)) &&
                        <Button
                            type="button"
                            color="success"
                            disabled={saving}
                            id="addChargesButton"
                            onClick={() => this.onAddChargeClicked()}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={
                                    saving ? faCircleNotch : faPlus
                                }
                                className="mr-2"
                            />
                            Add Internal Charge
                        </Button>
                    }

                    {unconfirmedCharges.length > 0 && (
                        <Button
                            type="button"
                            color="primary"
                            disabled={saving}
                            onClick={() => this.showSubmitChargesModal()}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={
                                    saving ? faCircleNotch : faCheckCircle
                                }
                                className="mr-2"
                            />
                            Submit Charges
                        </Button>
                    )}
                </FlexCenterRow>

                <Modal className="full-screen-modal"
                    backdrop="static"
                    keyboard={false}
                    isOpen={showSubmitChargesModal}>
                    <ModalHeader>
                        <FlexBetweenRow className="w-100">
                            <span className="title"><span className="fa fa-calendar"></span>&nbsp;&nbsp;{formattedDate}</span>
                            <span
                                className="fa fa-times fa-lg cursor-pointer"
                                onClick={() => this.setState({
                                    showSubmitChargesModal: false,
                                })}
                            />
                        </FlexBetweenRow>

                        
                    </ModalHeader>
                    <ModalBody>

                        <CollapseUnderlineHeader
                            headerText='Jobs'
                            isOpen={summaryJobsOpen}
                            toggleCollapse={() => this.setState({
                                summaryJobsOpen: !summaryJobsOpen,
                            })}>

                            <table width='100%'>
                                {jobs.map((d, idx) => (
                                    <tr>
                                        <td width='100%'>
                                            <span style={{ fontWeight: 'bold' }}>{d.customer} - {d.contractNumber}</span>

                                            <table width='100%'>

                                                {d.scaleTickets.map((st, idx) => (
                                                    <tr>
                                                        <td width='100%'>
                                                            <span>Ticket: #{st.workOrderNumber}:</span>

                                                            <table width='100%'>
                                                                {st.charges.map((c, idx) => (
                                                                    <>
                                                                        {c.chargeTypeUnitsId != 3 &&
                                                                            <tr>
                                                                                <td width='40%'>&nbsp;&nbsp;&nbsp;&nbsp;{c.chargeName}</td>
                                                                                <td width='20%'>{c.start != null ? moment(c.start).format('h:mm a') : '---'}</td>
                                                                                <td width='20%'>{c.end != null ? moment(c.end).format('h:mm a') : '---'}</td>
                                                                               
                                                                            </tr>
                                                                        }
                                                                    </>
                                                                ))}
                                                            </table>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </table>
                                        </td>
                                    </tr>
                                ))}
                            </table>
                            {jobs.length == 0 &&
                                <div>No Jobs</div>
                            }

                        </CollapseUnderlineHeader>
                        <p></p>
                        <CollapseUnderlineHeader
                            headerText='Internal Charges'
                            isOpen={summaryChargesOpen}
                            toggleCollapse={() => this.setState({
                                summaryChargesOpen: !summaryChargesOpen,
                            })}>

                            <table width='100%'>
                                <tr>
                                    <td width='100%'>
                                        <table width='100%'>
                                            <tr>
                                                <td width='100%'>
                                                    <table width='100%'>
                                                        {charges.map((d, idx) => (
                                                            <>
                                                                <tr>
                                                                    <td width='40%'>{d.chargeTypeName}</td>
                                                                    <td width='20%'>{moment(d.startTime, ['HH:mm']).format('h:mm a')}</td>
                                                                    <td width='20%'>{moment(d.endTime, ['HH:mm']).format('h:mm a')}</td>
                                                            
                                                                </tr>
                                                            </>
                                                        ))}
                                                    </table>

                                                </td>
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                            </table>
                            
                            {charges.length == 0 &&
                                <div>No Charges</div>
                            }

                        </CollapseUnderlineHeader>


                    </ModalBody>
                    <ModalFooter>

                        <table width='100%' style={{ marginBottom: '8px' }}>
                            <tr>
                                <td width='100%' align='center' style={{ fontSize: '20px', fontWeight: 'bold' }}>Total Hours: {this.getTotalChargeHours()}</td>
                            </tr>
                        </table>

                        <FlexColumnStart className="w-100">

                            <FlexCenterRow className="w-100">
                                <Button
                                    type="button"
                                    color="success"
                                    disabled={saving}
                                    onClick={() => this.submitInternalCharges()}
                                >
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={
                                            saving ? faCircleNotch : faCheckCircle
                                        }
                                        className="mr-2"
                                    />
                                    Submit Charges
                                </Button>
                            </FlexCenterRow>

                        </FlexColumnStart>
                    </ModalFooter>
                </Modal>

                <Modal
                    className="full-screen-modal"
                    backdrop="static"
                    keyboard={false}
                    isOpen={showAddChargeModal}
                >
                    <ModalHeader>
                        <FlexBetweenRow className="w-100">
                            <span>
                                {!Number.isNaN(parseInt(editingTimeIndex))
                                    ? 'Edit Internal Charge'
                                    : 'Add Internal Charge'}
                            </span>
                            <span
                                className="fa fa-times fa-lg cursor-pointer"
                                onClick={() => this.setState({
                                    showAddChargeModal: false,
                                })}
                            />
                        </FlexBetweenRow>
                    </ModalHeader>
                    <FormValidated
                        className="modal-body add-self-dispatch-time-form d-flex flex-column h-100 min-height-0"
                        id="addSelfDispatchTimeForm"
                        name="addSelfDispatchTimeForm"
                        ref={this.addTimeFormRef}
                        onSubmit={this.onAddTimeSubmit}
                        setIsValidated={(validated) => this.setState({ addTimeFormValidated: validated })}
                        isValidated={addTimeFormValidated}
                    >
                        <div className="d-flex flex-fill flex-column overflow-y-auto time-entry-content">
                            <FormGroup>
                                <FormLabel
                                    htmlFor="chargeTypesDropdown"
                                    text="Charge"
                                    required
                                />
                                <ValidatedSelect
                                    menuPosition="fixed" /* RLC: When being in an overflow container, this will prevent cutoff. */
                                    maxMenuHeight={150}
                                    isClearable={false}
                                    name="chargeTypesDropdown"
                                    required
                                    isDisabled={!!selectedDetail?.id}
                                    value={selectedContractChargeType}
                                    options={
                                        selectedContract?.contractChargeTypes
                                        ?? []
                                    }
                                    onChange={this.onChargeTypeChanged}
                                    getOptionLabel={(o) => o.description}
                                    getOptionValue={(o) => o.chargeTypeId}
                                    validationMessage="A charge is required."
                                />
                            </FormGroup>

                            {selectedContractChargeType?.id > 0
                                && (selectedContractChargeType.accountCodingTypeId == 2 || selectedContractChargeType.accountCodingTypeId == 5 || selectedContractChargeType.accountCodingTypeId == 4) &&
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="equipmentDropdown"
                                        text="Equipment"
                                        required
                                    />
                                    <ValidatedSelect
                                        menuPosition="fixed" /* RLC: When being in an overflow container, this will prevent cutoff. */
                                        maxMenuHeight={150}
                                        isClearable={false}
                                        name="equipmentDropdown"
                                        required
                                        value={selectedEquipment}
                                        options={
                                            equipment
                                            ?? []
                                        }
                                        getOptionLabel={(o) => o.description}
                                        getOptionValue={(o) => o.id}
                                        validationMessage="Equipment is required."
                                        onChange={this.onEquipmentChanged}
                                    />
                                </FormGroup>
                            }

                            {selectedContractChargeType?.chargeUnits
                                === 'Hours' && (
                                <>
                                    <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                        <FormLabel
                                            className="mb-0"
                                            htmlFor="start"
                                            text="Start Time"
                                        />
                                        <FlexColumnStart className="w-100">
                                            <TimeEntry
                                                name="start"
                                                value={selectedDetail.start}
                                                increment={selectedIncrement}
                                                onChange={this.onChargeChange}
                                            />
                                        </FlexColumnStart>
                                    </FormGroup>
                                    <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                        <FormLabel
                                            className="mb-0 flex-fill"
                                            htmlFor="chargeEndTime"
                                            text="End Time"
                                        />
                                        <FlexColumnStart className="w-100">
                                            <TimeEntry
                                                name="end"
                                                value={selectedDetail.end}
                                                increment={selectedIncrement}
                                                onChange={this.onChargeChange}
                                            />
                                        </FlexColumnStart>
                                    </FormGroup>

                                    <div className="p-0 mb-2 border-top" />
                                    <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                        <FormLabel
                                            className="mb-0 w-100"
                                            htmlFor="totalDuration"
                                            text="Total"
                                            required={false}
                                        />
                                        <div className="d-flex flex-row flex-nowrap align-items-center justify-content-start">
                                            <span
                                                id="totalDuration"
                                                className="h3 font-weight-bold text-muted m-0"
                                            >
                                                {this.getDuration(
                                                    timesheet?.date,
                                                    selectedDetail?.start,
                                                    selectedDetail?.end,
                                                    selectedDetail?.breakStart,
                                                    selectedDetail?.breakEnd,
                                                    true,
                                                )}
                                            </span>
                                        </div>
                                    </FormGroup>
                                    <div className="p-0 mb-2 border-top" />
                                </>
                            )}
                        </div>
                        <FlexColumnStart className="w-100">
                         
                            <FlexCenterRow className="w-100">
                                <Button
                                    name="addSelfDispatchTimeForm"
                                    className="w-100 d-flex flex-row flex-nowrap align-items-center justify-content-center"
                                    color="primary"
                                    onClick={this.onUpdateCharge}
                                >
                                    Save Charge
                                </Button>
                            </FlexCenterRow>
                            
                        </FlexColumnStart>
                    </FormValidated>
                </Modal>

                <Modal
                    isOpen={showRemoveInternalChargeModal}
                    toggle={this.toggleRemoveChargeModal}>

                    <ModalHeader toggle={this.toggleRemoveChargeModal}>
                        Remove Internal Charge
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <p className="text-danger">
                                Are you sure you wish to remove this charge?
                            </p>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="danger"
                            onClick={
                                this.onRemoveInternalCharge
                            }
                        >
                            Remove
                        </Button>
                        {' '}
                        <Button
                            color="secondary"
                            onClick={
                                this.toggleRemoveChargeModal
                            }
                        >
                            Cancel
                        </Button>
                        {' '}
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}

export default withRouter(DailyDrivingLogForm);